import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Download, ArrowUpRight, ArrowRight, Layers, ShieldCheck, Microscope, Truck, Camera, FileCheck, Sparkles } from "lucide-react";
import { BrandImage } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Detailed clinical prep tabs (from old Products page) ---------- */
const CLINICAL_PRODUCTS = {
    "permanent-crown": {
        name: "Permanent Crown",
        tagline: "Full-contour restoration · 10-year warranty",
        image: "https://images.unsplash.com/photo-1771442873035-474765b40ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
        indications: [
            "Single-unit posterior and anterior restorations",
            "Endodontically treated teeth requiring full coverage",
            "Replacement of failed PFM or zirconia crowns",
            "Bruxism patients (with night guard)",
        ],
        contraindications: [
            "Long-span bridges (>3 units)",
            "Subgingival margins below 1.5 mm",
            "Severe parafunction without occlusal protection",
        ],
        prep: { margin: "1.0 mm chamfer or shoulder", occlusal: "1.5–2.0 mm reduction", axial: "1.0–1.2 mm reduction", taper: "6–10° total convergence" },
        turnaround: "6–9 working hours",
    },
    "same-day-crown": {
        name: "Same-Day Crown",
        tagline: "Single-visit dentistry · Doha-area express",
        image: "https://images.unsplash.com/photo-1616996690916-a4d9af24295d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
        indications: [
            "Emergency single-unit restorations",
            "Patients requiring single-visit completion",
            "Posterior and pre-molar units",
        ],
        contraindications: [
            "Multi-unit cases (use Permanent Crown workflow)",
            "Cases received after 11:00 IST",
            "Anterior cases requiring shade layering",
        ],
        prep: { margin: "1.0 mm chamfer", occlusal: "1.5 mm reduction", axial: "1.0 mm reduction", taper: "8–10° total convergence" },
        turnaround: "Dispatch by 18:00 IST same day",
    },
    veneers: {
        name: "Veneers",
        tagline: "0.3–0.5 mm Nano Hybrid laminates",
        image: "https://images.unsplash.com/photo-1776406987595-ba14f3510c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
        indications: [
            "Anterior aesthetic corrections",
            "Discolouration not amenable to bleaching",
            "Diastema closure (≤2 mm)",
            "Minor malposition correction",
        ],
        contraindications: [
            "Edge-to-edge or Class III occlusion",
            "Severe bruxism",
            "Inadequate enamel for bonding",
        ],
        prep: { margin: "Feather-edge or chamfer (0.3 mm)", facial: "0.3–0.5 mm reduction", incisal: "1.0–1.5 mm reduction", taper: "Minimal — preserve enamel" },
        turnaround: "10–14 working hours",
    },
    "inlay-onlay": {
        name: "Inlays / Onlays",
        tagline: "Conservative restorations",
        image: "https://images.pexels.com/photos/36101264/pexels-photo-36101264.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        indications: [
            "Class II cavities too large for direct composite",
            "Replacement of failed amalgam restorations",
            "Cusp coverage in cracked tooth syndrome (onlay)",
        ],
        contraindications: [
            "Subgingival margins >2 mm",
            "Cases requiring full cusp protection (use crown)",
        ],
        prep: { margin: "90° butt-joint cavosurface", isthmus: ">2 mm width", depth: "2–3 mm pulpal floor", taper: "Diverging walls 6–8°" },
        turnaround: "6–8 working hours",
    },
};
const CLINICAL_KEYS = Object.keys(CLINICAL_PRODUCTS);

/* ---------- Full visual catalog (from old Catalog page) ---------- */
const CATALOG_CATEGORIES = [
    {
        id: "restorative",
        name: "Restorative",
        eyebrow: "· Nano Ceramic Hybrid · 10-yr warranty",
        intro: "Permanent and same-day restorations — milled-grade fit, shade-mapped to natural dentition, shipped with a 10-year clinical warranty against fracture, debonding, and chipping.",
        products: [
            { name: "FluxCeram™ Permanent Crown", desc: "Full-contour Nano Hybrid Ceramic crown for posterior and anterior units. 1.0 mm chamfer prep, shade-layered enamel translucency, polished glaze finish.", sla: "9–24 h" },
            { name: "FluxCeram™ Same-Day Crown", desc: "Single-visit posterior crown for emergencies and convenience patients. Cases received before 11:00 IST dispatch the same evening.", sla: "9 h" },
            { name: "FluxCeram™ Veneer", desc: "0.3–0.5 mm anterior laminate veneer. Hand-layered shade mapping with internal translucency stops for natural light play.", sla: "24 h" },
            { name: "FluxCeram™ Inlay / Onlay", desc: "Cusp-preserving alternative to full-coverage crowns. 90° butt-joint cavosurface margin, ≥ 2 mm isthmus.", sla: "18 h" },
        ],
        img: "/products/restorative.png",
    },
    {
        id: "orthodontic",
        name: "Orthodontic & Splints",
        eyebrow: "· Printed appliances",
        intro: "Clear retainers, soft and hard splints, indirect-bonding trays, and printed aligner stages — each fabricated in an indication-specific resin so the appliance behaves the way the clinician needs it to.",
        products: [
            { name: "FlexGuard™ Soft Splint", desc: "Resilient occlusal splint for bruxism and TMJ therapy. Shore-A elastomer, ≤ 2 mm wall thickness, polished occlusal table.", sla: "24 h" },
            { name: "RigidGuard™ Hard Splint", desc: "High-stiffness Michigan-style night guard. Full-arch coverage, planar occlusal stops, slow-wear surface.", sla: "24 h" },
            { name: "OrthoCast™ IBT Tray", desc: "Indirect-bonding tray for fixed-appliance positioning. Digitally planned bracket placement transferred to a flexible carrier.", sla: "18 h" },
            { name: "ClearArc™ Aligner", desc: "Sequential clear aligner stages, fabricated in-house. 0.75 mm thermoplastic, scalloped sealed margins.", sla: "Per stage" },
            { name: "MemoryHold™ Retainer", desc: "Shape-memory rigid retainer that returns to its programmed geometry at body temperature (R&D · Project M-12).", sla: "36 h" },
        ],
        img: "/products/orthodontic.png",
    },
    {
        id: "surgical",
        name: "Surgical & Models",
        eyebrow: "· Pre-op planning",
        intro: "Pilot drill guides, fully-guided implant guides, and anatomical models for case presentation and surgical rehearsal — printed from your DICOM + STL fusion files.",
        products: [
            { name: "ProDrill™ Surgical Guide", desc: "Tooth- or bone-supported guide with stainless-steel sleeves. Pilot or fully-guided protocols.", sla: "24 h" },
            { name: "ProModel™ Study Model", desc: "Diagnostic working model — high-detail surface (50 µm layer height), die-pin compatible.", sla: "12 h" },
            { name: "ProModel™ Soft-Tissue Mask", desc: "Removable gingival mask for crown try-in. Realistic translucency and tactile feedback.", sla: "12 h" },
        ],
        img: "/products/surgical.png",
    },
    {
        id: "sports",
        name: "Sports & Performance",
        eyebrow: "· Team programs",
        intro: "Impact-tuned mouthguards calibrated to the specific sport and athlete. Team colours, club logos, and NFC/RFID player-ID tagging available on bulk orders.",
        products: [
            { name: "ImpactX™ Pro Mouthguard", desc: "High-energy spec for boxing / MMA / rugby. Triple-laminate elastomer, anterior block.", sla: "48 h" },
            { name: "ImpactX™ Performance Guard", desc: "Hockey, basketball, and contact field sports. Lower bulk, full speech tolerance.", sla: "48 h" },
            { name: "ImpactX™ Team Series", desc: "Bulk runs with team colour, club logo, and per-player ID tagging. Min. 10 units.", sla: "Per batch" },
        ],
        img: "/products/sports.png",
    },
    {
        id: "jewellery",
        name: "Dental Jewellery & Bespoke",
        eyebrow: "· Reversible · cultural",
        intro: "Decorative and try-before-you-commit dental appliances printed to clinical-grade biocompatibility. Removable, repositionable, radiopaque — and never invasive to the underlying enamel.",
        products: [
            { name: "Snap-Fit Veneer Arch™", desc: "Custom-printed full-arch resin shell that snaps over existing teeth. Non-invasive, 1–5 year service life (R&D · SF-01, launching Q4 2026).", sla: "Pre-launch" },
            { name: "Lumina™ Crystal Inset", desc: "Swarovski-faceted crystal mount, applied single-visit. Removable mount geometry, no enamel insult (R&D · J-09).", sla: "Single visit" },
            { name: "Diamantine™ Slip-on Veneer", desc: "Micro-diamond-embedded reversible veneer shell. 0.3 mm hybrid laminate base (R&D · D-04).", sla: "Bespoke" },
            { name: "Gilt™ Gold-leaf Caps", desc: "Cultural / traditional gold-leaf surfacing, applied to printed micro-mounts.", sla: "Bespoke" },
        ],
        img: "/products/jewellery.png",
    },
];

function CatalogImage({ slug, fallback, alt }) {
    return (
        <BrandImage
            slug={slug}
            fallback={fallback}
            alt={alt}
            className="w-full h-[460px] object-cover"
        />
    );
}

const SECTION_NAV = [
    { id: "restoratives", label: "Restoratives", icon: ShieldCheck },
    { id: "orthodontic", label: "Orthodontic", icon: Layers },
    { id: "surgical", label: "Surgical", icon: FileCheck },
    { id: "sports", label: "Sports", icon: Truck },
    { id: "jewellery", label: "Bespoke", icon: Sparkles },
];

export default function ForClinics() {
    const [active, setActive] = useState(CLINICAL_KEYS[0]);
    const p = CLINICAL_PRODUCTS[active];

    return (
        <div data-testid="forclinics-page" className="bg-white">
            {/* HERO */}
            <section className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="tr-overline mb-4">· For Clinics · Toothrocket lab</div>
                        <h1 className="font-display font-light text-5xl lg:text-7xl tracking-tight leading-[0.98] text-slate-900">
                            Every restoration<br />
                            <span className="italic">we make.</span>
                        </h1>
                        <p className="mt-6 text-[16px] text-slate-600 leading-[1.75] max-w-2xl">
                            One Nano Ceramic Hybrid Resin matrix · five product families · a 10-year clinical warranty on every restoration we ship. Clinical prep guidelines and the full visual catalog below — pick a category to dive in.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
                            <div className="flex items-start gap-3">
                                <ShieldCheck size={20} className="text-cyan-600 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-display text-[15px] text-slate-900">10-year clinical warranty</div>
                                    <div className="text-[12.5px] text-slate-500">Fracture · debonding · chipping</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Microscope size={20} className="text-cyan-600 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-display text-[15px] text-slate-900">9–24h turnaround</div>
                                    <div className="text-[12.5px] text-slate-500">Same-day on cases by 11:00</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Camera size={20} className="text-cyan-600 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-display text-[15px] text-slate-900">Digital-first workflow</div>
                                    <div className="text-[12.5px] text-slate-500">STL · DICOM · intra-oral scans</div>
                                </div>
                            </div>
                            <Link to="/workflow#send-case" className="tr-btn w-full justify-center mt-2" data-testid="hero-send-case">
                                Send a case <ArrowUpRight size={15} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* STICKY CATEGORY NAV */}
            <nav className="sticky top-[68px] z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200" data-testid="forclinics-nav">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 flex gap-1 overflow-x-auto">
                    {SECTION_NAV.map((s) => {
                        const Icon = s.icon;
                        return (
                            <a key={s.id} href={`#${s.id}`} className="flex items-center gap-2 px-4 py-3 text-[13px] font-medium text-slate-600 hover:text-slate-900 whitespace-nowrap transition">
                                <Icon size={13} className="text-cyan-600" />
                                {s.label}
                            </a>
                        );
                    })}
                </div>
            </nav>

            {/* SECTION 1 — Restoratives (clinical prep detail) */}
            <section id="restoratives" className="scroll-mt-32 max-w-[1280px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
                <div className="mb-10">
                    <div className="tr-overline mb-3">· Restorative · clinical detail</div>
                    <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900">
                        Pick the indication.<br />
                        <span className="italic">We'll show you the prep.</span>
                    </h2>
                </div>

                <div className="grid lg:grid-cols-12 gap-10" data-testid="clinical-tabs">
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-[140px] space-y-1">
                            {CLINICAL_KEYS.map((k) => {
                                const item = CLINICAL_PRODUCTS[k];
                                const isActive = k === active;
                                return (
                                    <button
                                        key={k}
                                        data-testid={`tab-${k}`}
                                        onClick={() => setActive(k)}
                                        className={`w-full text-left px-4 py-4 rounded-xl border transition flex flex-col gap-1 ${
                                            isActive
                                                ? "bg-[#0B1120] text-white border-[#0B1120]"
                                                : "bg-white text-slate-900 border-slate-200 hover:border-cyan-300"
                                        }`}
                                    >
                                        <div className="font-display text-lg tracking-tight">{item.name}</div>
                                        <div className={`text-[11.5px] font-mono-tr tracking-[0.16em] uppercase ${isActive ? "text-cyan-300" : "text-slate-500"}`}>
                                            {item.turnaround}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    <div className="lg:col-span-9">
                        <div className="grid lg:grid-cols-2 gap-8 items-start">
                            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 relative">
                                <BrandImage slug={`products_${active}`} fallback={p.image} alt={p.name} className="w-full h-[420px] object-cover" />
                                <div className="absolute top-4 left-4 px-2.5 py-1 rounded-md bg-white/95 text-[10px] font-mono-tr tracking-[0.2em] uppercase text-slate-900">
                                    Render · {p.name}
                                </div>
                            </div>
                            <div>
                                <h3 className="font-display text-3xl lg:text-4xl tracking-tight text-slate-900">{p.name}</h3>
                                <div className="mt-2 font-mono-tr text-[12px] tracking-[0.18em] uppercase text-cyan-700">{p.tagline}</div>

                                <div className="mt-7 grid grid-cols-2 gap-3">
                                    {Object.entries(p.prep).map(([k, v]) => (
                                        <div key={k} className="rounded-lg border border-slate-200 bg-white p-3">
                                            <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">{k}</div>
                                            <div className="mt-1 text-[14px] text-slate-900 font-medium">{v}</div>
                                        </div>
                                    ))}
                                </div>

                                <Link to="/workflow#send-case" data-testid={`prescribe-${active}`} className="tr-btn mt-7">
                                    Prescribe this case <ArrowUpRight size={16} />
                                </Link>
                            </div>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-6 mt-12">
                            <div className="rounded-2xl border border-slate-200 p-7 bg-white">
                                <div className="tr-overline mb-3 text-emerald-700">· Indications</div>
                                <ul className="space-y-3">
                                    {p.indications.map((it) => (
                                        <li key={it} className="flex items-start gap-3 text-[14.5px] text-slate-700 leading-relaxed">
                                            <Check size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-2xl border border-slate-200 p-7 bg-slate-50/40">
                                <div className="tr-overline mb-3 !text-rose-700">· Contraindications</div>
                                <ul className="space-y-3">
                                    {p.contraindications.map((it) => (
                                        <li key={it} className="flex items-start gap-3 text-[14.5px] text-slate-700 leading-relaxed">
                                            <X size={18} className="text-rose-500 mt-0.5 shrink-0" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <div className="font-display text-lg tracking-tight text-slate-900">Preparation guideline</div>
                                <div className="text-[13px] text-slate-600 mt-0.5">Margin design, reduction, and try-in protocol for {p.name}. With clinical video demos.</div>
                            </div>
                            <Link data-testid="prep-pdf-btn" to={`/prep-guidelines#${active.includes("crown") ? (active === "permanent-crown" ? "posterior-crown" : "posterior-crown") : (active === "veneers" ? "veneer" : "inlay")}`} className="tr-btn-ghost">
                                <ArrowUpRight size={16} /> Open guide
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2-5 — Full visual catalog */}
            <section className="border-t border-slate-200 bg-slate-50/40">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
                    <div className="mb-12">
                        <div className="tr-overline mb-3">· Full catalog</div>
                        <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900">
                            Beyond restorative.<br />
                            <span className="italic">Everything we print.</span>
                        </h2>
                    </div>

                    <div className="space-y-20" data-testid="catalog-categories">
                        {CATALOG_CATEGORIES.filter((c) => c.id !== "restorative").map((c) => (
                            <section key={c.id} id={c.id} className="scroll-mt-32 grid lg:grid-cols-12 gap-10">
                                <div className="lg:col-span-5">
                                    <div className="rounded-[24px] overflow-hidden border border-slate-200 lg:sticky lg:top-[140px]">
                                        <CatalogImage slug={c.id} fallback={c.img} alt={c.name} />
                                    </div>
                                </div>
                                <div className="lg:col-span-7">
                                    <div className="tr-overline mb-3">{c.eyebrow}</div>
                                    <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight leading-[1.05] text-slate-900">{c.name}</h3>
                                    <p className="mt-5 text-[15px] text-slate-600 leading-[1.75]">{c.intro}</p>
                                    <ul className="mt-8 space-y-3">
                                        {c.products.map((prod) => (
                                            <li key={prod.name} className="tr-card p-5 flex items-start justify-between gap-5">
                                                <div className="flex items-start gap-3.5 min-w-0">
                                                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 shrink-0">
                                                        <Layers size={16} />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-display text-[17px] text-slate-900 tracking-tight">{prod.name}</div>
                                                        <div className="text-[13px] text-slate-600 mt-0.5 leading-relaxed">{prod.desc}</div>
                                                    </div>
                                                </div>
                                                <div className="font-mono-tr text-[11px] tracking-[0.18em] uppercase text-cyan-700 whitespace-nowrap pt-2">
                                                    {prod.sla}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </section>

            {/* BOTTOM CTAs */}
            <section className="border-t border-slate-200 bg-white">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8">
                        <h3 className="font-display font-light text-4xl tracking-tight text-slate-900 leading-tight">
                            Don&rsquo;t see what you need?<br />Ask us to build it.
                        </h3>
                        <p className="mt-3 text-slate-600 text-[15px] max-w-2xl">
                            Our R&amp;D team takes one new applied-science brief every month. Sports, jewellery, materials, or something else entirely.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:flex lg:justify-end gap-3 flex-wrap">
                        <Link to="/rnd" className="tr-btn-ghost">See R&amp;D <ArrowRight size={16} /></Link>
                        <Link to="/workflow#send-case" className="tr-btn">Send a Case <ArrowUpRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
